import { Injectable, signal } from "@angular/core";
import { environment } from "../../../environments/environment";
import { DatePipe } from "@angular/common";
import { DashbaordPocAdditionalFilters } from "../models/common/dashboardPocAdditionalFilters.dto";
import { BehaviorSubject, Observable } from "rxjs";
import { GenericResponseDto } from "../models/generic-response.dto";
import { NotificationsDto } from "../models/common/notification.dto";
import { InventoryAccessDto } from "../models/common/inventoryAccessDto.dto";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import FileSaver from "file-saver";
import { ClientSideLineTrackingListDto } from "../models/project-workflow/clientSideLineTrackingListDto";

@Injectable({
  providedIn: "root",
})
export class SharedService {
  constructor(
    private _http: HttpClient,
    public _datePipe: DatePipe
  ) {
    this.initializeTheme();
  }

  private saleOrderManagementBaseURL =
    environment.baseUrl + "/SalesOrderManagement";
  private commonBaseURL = environment.baseUrl + "/Common";
  private chatBoxBaseURL = environment.baseUrl + "/ChatBox";
  private userSupportManagementBaseURL = environment.baseUrl + "/UserSupport";

  dashbaordPocAdditionalFilters: DashbaordPocAdditionalFilters =
    {} as DashbaordPocAdditionalFilters;

  simpleOrderSearchResponseObject: string[] = [];

  private orderSearchValue = "";
  private sampleWorkflowSubject = new BehaviorSubject<number>(0);

  private themeSignal = signal<"light" | "dark">("light");

  searchTextValueBehaviorSubject = new BehaviorSubject("");
  searchTextPlaceholderBehaviorSubject = new BehaviorSubject("");

  taskSearchText = signal("");

  setTaskSearchText(value: string) {
    this.taskSearchText.set(value);
  }

  getTaskSearchText(): string {
    return this.taskSearchText();
  }

  clearTaskSearchText() {
    this.taskSearchText.set("");
  }

  setDashbaordPocAdditionalFiltersObject(obj: DashbaordPocAdditionalFilters) {
    this.dashbaordPocAdditionalFilters = obj;
  }

  getDashbaordPocAdditionalFiltersObject() {
    return this.dashbaordPocAdditionalFilters;
  }

  initializeTheme() {
    const savedTheme = localStorage.getItem("theme") as "light" | "dark";
    if (savedTheme) {
      this.themeSignal.set(savedTheme);
    }
  }

  getTheme() {
    return this.themeSignal;
  }

  setTheme(theme: "light" | "dark") {
    this.themeSignal.set(theme);
  }

  clearDashbaordPocAdditionalFiltersObject() {
    this.dashbaordPocAdditionalFilters = {} as DashbaordPocAdditionalFilters;
  }

  getSimpleOrderSearch(
    searchedText: string
  ): Observable<GenericResponseDto<string[]>> {
    return this._http.get<GenericResponseDto<string[]>>(
      this.saleOrderManagementBaseURL +
        `/SimpleOrderSearch?searchedText=` +
        searchedText
    );
  }

  setSimpleOrderSearchResponseObject(obj: string[]) {
    this.simpleOrderSearchResponseObject = obj;
  }

  getSimpleOrderSearchResponseObject() {
    return this.simpleOrderSearchResponseObject;
  }

  setOrderSearchValue(value: string) {
    this.orderSearchValue = value;
    this.searchTextValueBehaviorSubject.next(this.orderSearchValue);
  }

  getOrderSearchValue() {
    return this.orderSearchValue;
  }

  setOrderSearchPlaceholderValue(value: string) {
    this.searchTextPlaceholderBehaviorSubject.next(value);
  }

  convertDateFormat(date: string) {
    return this._datePipe.transform(date, "yyyy-MM-dd") ?? "";
  }

  getNotifications(
    chatId: number,
    getAll = false,
    moduleName: string
  ): Observable<GenericResponseDto<NotificationsDto[]>> {
    return this._http.get<GenericResponseDto<NotificationsDto[]>>(
      this.chatBoxBaseURL +
        `/GetNotifications?chatId=${chatId}&GetAll=${getAll}&ModuleName=${moduleName}`
    );
  }

  markNotificationMarkIsRead(
    notificationId: number
  ): Observable<GenericResponseDto<string>> {
    return this._http.post<GenericResponseDto<string>>(
      this.chatBoxBaseURL + "/MarkNotificationIsRead",
      {
        notificationId: notificationId,
      }
    );
  }

  updateSampleWorkflow(id: number) {
    this.sampleWorkflowSubject.next(id);
  }

  getSampleWorkflow() {
    return this.sampleWorkflowSubject.asObservable();
  }

  sortTrackingData(
    trackingDetailsList: ClientSideLineTrackingListDto[]
  ): ClientSideLineTrackingListDto[] {
    // Step 1: Organize the data
    const masterTrackingRows = trackingDetailsList.filter(
      row => row.isMasterTracking
    );
    const nonMasterTrackingRows = trackingDetailsList.filter(
      row => !row.isMasterTracking
    );

    // Step 2: Create a mapping from MasterTrackingNumber to related non-master tracking rows
    const trackingMap: Record<string, ClientSideLineTrackingListDto[]> = {};
    nonMasterTrackingRows.forEach(row => {
      const masterTrackingNumber = row.masterTrackingNumber;
      if (masterTrackingNumber) {
        if (!trackingMap[masterTrackingNumber]) {
          trackingMap[masterTrackingNumber] = [];
        }
        trackingMap[masterTrackingNumber].push(row);
      }
    });

    // Step 3: Combine the data
    const sortedData: ClientSideLineTrackingListDto[] = [];
    masterTrackingRows.forEach(masterRow => {
      sortedData.push(masterRow);
      const masterTrackingNumber = masterRow.trackingNumber;
      if (trackingMap[masterTrackingNumber]) {
        sortedData.push(...trackingMap[masterTrackingNumber]);
      }
    });

    // Add non-master tracking rows that weren't included in the mapping
    const includedTrackingNumbers = masterTrackingRows.map(
      row => row.trackingNumber
    );
    const nonMasterRowsToInclude = nonMasterTrackingRows.filter(
      row => !includedTrackingNumbers.includes(row.masterTrackingNumber ?? "")
    );
    sortedData.push(...nonMasterRowsToInclude);

    return sortedData; // Output sorted data for verification
  }

  getNotificationProjectDetails(
    notificationId: number
  ): Observable<GenericResponseDto<string>> {
    return this._http.get<GenericResponseDto<string>>(
      this.chatBoxBaseURL +
        `/GetNotificationProjectDetails?notificationId=${notificationId}`
    );
  }

  getDisplayMobilePopUpValue() {
    return localStorage.getItem("MobilePopUpDisplay") ?? "true";
  }

  isInventoryAccess(): Observable<GenericResponseDto<InventoryAccessDto>> {
    return this._http.get<GenericResponseDto<InventoryAccessDto>>(
      this.userSupportManagementBaseURL + `/IsInventoryAccess`
    );
  }

  enumToList<T extends Record<string, number | string>>(
    enumObj: T
  ): { name: string; value: number }[] {
    const list = Object.keys(enumObj)
      .filter(key => isNaN(Number(key))) // Filter out numeric keys
      .map(key => ({
        name: key,
        value: enumObj[key as keyof T] as number,
      }));

    return list;
  }

  getFileAsBlob(fileURL: string) {
    return this._http.get(fileURL, {
      responseType: "blob",
      withCredentials: true,
    });
  }

  /* eslint-disable @typescript-eslint/no-explicit-any */
  deepCompare(obj1: any, obj2: any): boolean {
    const keys1 = Object.keys(obj1);
    /* eslint-enable @typescript-eslint/no-explicit-any */
    if (obj1 && obj2) {
      for (const key of keys1) {
        if (key !== "stackedDataOptions" && key !== "chartOptions") {
          if (obj1[key] !== obj2[key]) {
            return true;
          }
        }
      }
    }

    return false;
  }
}
export function payloadHeader<T>(payload: T) {
  const options = {
    headers: new HttpHeaders({
      "Content-Type": "application/json",
    }),
    body: payload,
  };
  return options;
}

export function getBase64(file: Blob) {
  return new Promise((resolve, reject) => {
    if (typeof file !== "number") {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    }
  });
}

export function exportExcel<T>(gridData: T[], fileName: string) {
  import("xlsx").then(xlsx => {
    const worksheet = xlsx.utils.json_to_sheet(gridData);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
    const excelBuffer = xlsx.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    saveAsExcelFile(excelBuffer, fileName);
  });
}

export function saveAsExcelFile(buffer: string, fileName: string): void {
  const EXCEL_TYPE =
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const EXCEL_EXTENSION = ".xlsx";
  const data: Blob = new Blob([buffer], {
    type: EXCEL_TYPE,
  });
  FileSaver.saveAs(
    data,
    fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
  );
}
